import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Channel"` — unit P11-06. **All three states ship.**
 *
 *   connected      stats, the connection list, the widget script tag, Disconnect;
 *   coming-soon    what the hold-up is, whose it is, and that there is nothing to prepare;
 *   not-connected  the numbered steps, the fields and the Connect panel.
 *
 * The state is the API's, not this screen's: `coming_soon` outranks the others because a
 * waitlisted medium cannot carry a conversation whatever bindings exist.
 *
 * TWO PLACES THE DESIGN'S COPY CHANGED:
 *
 *   1. `CHANNEL_DETAIL.voice.rows` reads `Method: SIP trunk · WebRTC endpoint live` and
 *      `Inbound URI: sip:roma-pizzeria@sip.wetalk.io`. Neither ships. The browser media plane
 *      is spike S8's open question — WebRTC needs ICE/DTLS/SRTP over UDP and Container Apps
 *      has no UDP ingress — so there is no live browser endpoint to advertise, and the SIP
 *      host is the carrier's (plan amendment A7). Voice therefore sends the reader to
 *      Numbers & SIP, which is the one screen that holds those facts and keeps them correct.
 *   2. `Region: us-west · 4 ms` is not repeated here at all. It is a WeTalk measurement, it
 *      lives beside the trunk it was measured for, and duplicating it onto a second screen is
 *      how one of the two copies goes stale.
 *
 * LAID OUT AS THE DESIGN DRAWS IT (ADR 0018). The connected state's Behaviour card keeps the
 * design's place: which agent answers is shown (it is the binding's), and the three switches —
 * recording, voicemail after hours, transfer — sit under it. "Rotate credentials" leads to
 * Numbers & SIP, where rotation lives; "Copy details" copies the facts this card shows.
 *
 * "RECORD CALLS" IS LIVE ON VOICE (plan-003 RC-47, product owner 2026-09-24: "add also the record
 * toggle", default on). It is the answering VoiceAgent's own setting — the agent this card names —
 * and it is pinned in that agent's versions, so flipping it publishes a new version
 * (`setVoiceAgentRecording`, quoting the live sequence like a publish). The design's words are
 * kept, "Recordings are kept 30 days, then deleted.", with the workspace's real recording
 * retention in place of the example's 30 days. After a change the card says which version now
 * answers and that calls already in progress keep the setting they started with. Voicemail and
 * transfer are still drawn disabled and marked "soon": no setting for them exists yet. On the
 * text media the design draws no switches at all, and recording is a voice-only fact, so all
 * three stay "soon" there.
 *
 * The widget key is **not a secret**: it is a `channel_binding_id`, pasted into the
 * customer's own page source, readable by every visitor, and carrying no scope. It is shown
 * in full because hiding a public identifier teaches people to treat it as a credential.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardTitle,
  Field,
  Icon,
  Input,
  Select,
  Tag,
} from "@wetalk/ui";
import { SwitchControl } from "../billing/switch-control.js";
import { displayNumberOf } from "../numbers/number-display.js";
import { useState, type ReactNode } from "react";

import "./messaging.css";

import { MEDIUM_GLYPH, MEDIUM_STATE_LABEL, MEDIUM_STATE_TONE } from "./messaging-screen.js";
import { SlowRead, WEB_CHAT_SLOW_NOTICE_MS, WebChatDetail, readProblemOf } from "./web-chat-detail.js";
import { GLYPH } from "../../shell/glyph.js";
import { rolesFor, rolesInPlainWords, useMemberCan } from "../../shell/member-capability.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, complianceQuery, messagingMediumQuery, voiceAgentQuery } from "../../api/query.js";
import type { MessagingMediumDetailView } from "../../api/gateway.js";
import type { ComplianceView } from "../../api/governance-view.js";
import { RETAIN_FOREVER_DAY, retainDayLabel } from "../../api/governance-view.js";
import { talkTimeText } from "../../talk-time.js";

export interface MediumScreenProps {
  readonly mediumCode: string;
  /** `?agent=` — the voice agent the reader came from (the Channels tab), pre-selected on web chat. */
  readonly requestedVoiceAgentId: string | null;
}

/**
 * AMENDED 2026-09-26 (plan-004 WC-81, FR-WC-15, review #175): web chat is its own page
 * (`web-chat-detail.tsx`) — every website widget, its sites, its snippet — and a failed read of any
 * medium shows what failed, the request id to quote and Retry, never "Opening the channel…" forever.
 */
export function MediumScreen({ mediumCode, requestedVoiceAgentId }: MediumScreenProps): ReactNode {
  usePortalTranslation("operations");
  if (mediumCode === "web_chat") {
    return <WebChatDetail requestedVoiceAgentId={requestedVoiceAgentId} slowNoticeMs={WEB_CHAT_SLOW_NOTICE_MS} />;
  }
  return <OtherMediumScreen mediumCode={mediumCode} />;
}

function OtherMediumScreen({ mediumCode }: { readonly mediumCode: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const query = messagingMediumQuery(gateway, mediumCode);
  const medium = useQuery(query);

  // Review #52, #175: a read that fails says so — with the request id and Retry — instead of
  // "Opening the channel…" forever.
  if (medium.isError) {
    const problem = readProblemOf(medium.error, portalText("operations", "the_channel_could_not_be_read_28c4c3ae"));
    return (
      <section className="c-page mg-detail-page">
        <Link to="/messaging" className="mg-backlink">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />
          {portalText("operations", "all_channels_844df142")}</Link>
        <div className="mg-problem" role="alert">
          <p className="mg-problem-title">{portalText("operations", "this_channel_could_not_be_opened_6cbc41f0")}</p>
          <p className="mg-card-note">
            {problem.text} {portalText("operations", "nothing_is_shown_rather_than_a_guess_and_nothing_has_chang_8bbdc2c2")}{problem.requestId === null ? null : (
              <>
                {" "}
                {portalText("operations", "request_id_cbe1c0a3")}<span className="mg-request-id">{problem.requestId}</span>.
              </>
            )}
          </p>
          <div className="mg-card-actions">
            <Button
              variant="primary"
              disabled={medium.isFetching}
              onClick={() => {
                void medium.refetch();
              }}
            >
              {medium.isFetching ? portalText("operations", "trying_again_4900bdd3") : portalText("operations", "retry_9f5cd8a2")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (medium.data === undefined) {
    return (
      <SlowRead
        opening="Opening the channel…"
        slowNoticeMs={WEB_CHAT_SLOW_NOTICE_MS}
        onRetry={() => {
          // A read with no answer yet is deduplicated by a plain refetch, so it is cancelled first.
          void queryClient.cancelQueries({ queryKey: query.queryKey }).then(() => medium.refetch());
        }}
      />
    );
  }

  const one = medium.data;

  return (
    <section className="c-page mg-detail-page">
      <Link to="/messaging" className="mg-backlink">
        <Icon glyph={GLYPH.arrowLeft} size="sm" />
        {portalText("operations", "all_channels_844df142")}</Link>

      <div className="mg-head">
        <span className="mg-head-mark">
          <Icon glyph={MEDIUM_GLYPH[one.medium]} size="3xl" />
        </span>
        <div className="mg-head-body">
          <div className="mg-head-title">
            <h1 className="mg-detail-title">{one.name}</h1>
            <Tag tone={MEDIUM_STATE_TONE[one.state]}>{MEDIUM_STATE_LABEL[one.state]}</Tag>
          </div>
          <p className="mg-blurb">{one.blurb}</p>
        </div>
        {one.state === "connected" ? <DisconnectButton medium={one} /> : null}
      </div>

      {one.state === "connected" ? (
        <ConnectedState medium={one} />
      ) : one.state === "coming_soon" ? (
        <ComingSoonState medium={one} />
      ) : (
        <NotConnectedState medium={one} />
      )}
    </section>
  );
}

/**
 * Disconnect, in the header where the design puts it. Voice is not disconnected here — its
 * number or trunk is unbound on Numbers & SIP — so for voice the button is disabled and says why.
 */
function DisconnectButton({ medium }: { readonly medium: MessagingMediumDetailView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const disconnect = useMutation({
    mutationFn: () => gateway.disconnectMessagingMedium(medium.medium),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.messagingMedium });
    },
  });
  return (
    <Button
      variant="secondary"
      disabled={medium.medium === "voice" || disconnect.isPending}
      title={
        medium.medium === "voice"
          ? portalText("operations", "voice_is_disconnected_by_unbinding_its_number_or_trunk_on_b4d69c38")
          : undefined
      }
      onClick={() => {
        disconnect.mutate();
      }}
    >
      {portalText("operations", "disconnect_ed28e068")}</Button>
  );
}

/** The three behaviour switches the design draws. Only "Record calls" has a setting, on voice. */
const BEHAVIOUR: readonly { readonly code: "record" | "voicemail" | "transfer"; readonly name: string; readonly dek: string }[] = [
  { code: "record", get name() { return portalText("operations", "record_calls_37ca242a"); }, get dek() { return portalText("operations", "keeps_a_recording_beside_each_transcript_2e39a0b5"); } },
  { code: "voicemail", get name() { return portalText("operations", "voicemail_after_hours_529df282"); }, get dek() { return portalText("operations", "takes_a_message_and_sends_you_a_recap_a16a4dd8"); } },
  { code: "transfer", get name() { return portalText("operations", "transfer_to_staff_af54440e"); }, get dek() { return portalText("operations", "hands_over_when_the_agent_cannot_answer_1c3c4f78"); } },
];

const RECORDING_SETTING_NAME = () => (portalText("operations", "record_calls_37ca242a"));

/**
 * The design's line, "Recordings are kept 30 days, then deleted.", with the workspace's chosen
 * recording retention instead of the example's figure. Until the retention has been read the line
 * names where it is set rather than guessing a number.
 *
 * AMENDED 2026-09-26 (customer-portal review #210): a role that may not read Compliance (a Member
 * or a Viewer) is never sent to ask `GET /v1/compliance`, and the line names who sets the period,
 * because "set on Compliance" points at a page that role is not offered.
 */
export function recordingRetentionText(
  compliance: ComplianceView | undefined,
  readsCompliance: boolean,
): string {
  if (!readsCompliance) {
    return portalText("operations", "recordings_are_kept_for_the_period_the_value_set_on_compli_05ee66bf", { v0: String(rolesInPlainWords(rolesFor("compliance.read"))) });
  }
  const recording = compliance?.retention.find((one) => one.class === "recording");
  if (recording === undefined) return portalText("operations", "recordings_are_kept_for_the_period_set_on_compliance_then_4de2e1ba");
  if (recording.chosenRetainDay === RETAIN_FOREVER_DAY) return portalText("operations", "recordings_are_kept_until_you_delete_them_632eb6fc");
  return portalText("operations", "recordings_are_kept_value_then_deleted_3bb39ed3", { v0: String(retainDayLabel(recording.chosenRetainDay)) });
}

/**
 * "Record calls" for the VoiceAgent that answers this voice channel (plan-003 RC-47).
 *
 * The switch shows the live version's answer and changes it by publishing a new version, quoting
 * the live sequence. It is disabled while that agent has no live version (its first build takes
 * the answer it was created with) and while a change is in flight. A refused change — somebody
 * else published first — changes nothing; the agent is re-read so the switch shows what is live.
 */
function RecordingSetting({ voiceAgentId }: { readonly voiceAgentId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));
  // Compliance is Owner or Admin only: nobody else asks for it (review #210).
  const readsCompliance = useMemberCan().can("compliance.read");
  const compliance = useQuery({ ...complianceQuery(gateway), enabled: readsCompliance });
  const [outcome, setOutcome] = useState<"published" | "refused" | null>(null);

  const change = useMutation({
    mutationFn: async (next: boolean) => {
      const current = voiceAgent.data;
      if (current === undefined) {
        throw new Error(portalText("operations", "the_agent_has_not_loaded_so_there_is_no_sequence_to_quote_5a9bf163"));
      }
      return gateway.setVoiceAgentRecording(voiceAgentId, next, current.liveVersionSeq);
    },
    async onSuccess(result) {
      setOutcome(result.changed ? "published" : null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    },
    async onError() {
      setOutcome("refused");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    },
  });

  const loaded = voiceAgent.data;
  const noLiveVersion = loaded !== undefined && loaded.liveAgentVersionId === null;

  return (
    <span className="mg-setting">
      <span className="mg-setting-body">
        <span className="mg-setting-name">{RECORDING_SETTING_NAME()}</span>
        <span className="mg-setting-dek">{recordingRetentionText(compliance.data, readsCompliance)}</span>
        {noLiveVersion ? (
          <span className="mg-setting-dek">
            {portalText("operations", "this_agent_is_not_live_yet_its_first_version_records_as_it_405712e0")}</span>
        ) : null}
        {outcome === "published" && loaded !== undefined ? (
          <span className="mg-setting-dek" role="status">
            {portalText("operations", "published_as_e90f1830")}{loaded.liveAgentVersionLabel}{portalText("operations", "calls_already_in_progress_keep_the_setting_they_started_wi_19d10e2e")}</span>
        ) : null}
        {outcome === "refused" ? (
          <span className="mg-setting-dek c-note-critical" role="alert">
            {portalText("operations", "that_was_not_saved_and_nothing_changed_the_switch_now_show_eeb1818b")}</span>
        ) : null}
      </span>
      <SwitchControl
        label={RECORDING_SETTING_NAME()}
        labelVisible={false}
        checked={loaded?.recordingEnabled === true}
        disabled={loaded === undefined || noLiveVersion || change.isPending}
        onChange={(next) => {
          change.mutate(next);
        }}
      />
    </span>
  );
}

function bindingLabel(kind: string): string {
  if (kind === "phone_number") return portalText("operations", "number_b7baa1d4");
  if (kind === "sip_user") return portalText("operations", "sip_user_5bce3de7");
  if (kind === "web_chat_widget") return portalText("operations", "widget_key_87b18f80");
  return kind;
}

function ConnectedState({ medium }: { readonly medium: MessagingMediumDetailView }): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState<"copied" | "refused" | null>(null);
  const grouped = (value: number): string => new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE")).format(value);
  const first = medium.binding[0];

  // AMENDED 2026-10-15 (design audit): the design's "Provider" row names the carrier, and a carrier is never named on
  // a customer surface, so the row is not drawn; the number's provider is on Numbers & SIP in the design's own words.
  const facts: readonly (readonly [string, string])[] = medium.binding.flatMap((binding) => [
    [bindingLabel(binding.kind), binding.kind === "phone_number" ? displayNumberOf(binding.address) : binding.address] as const,
    [portalText("operations", "agent_5ce2e6f4"), binding.voiceAgentName] as const,
  ]);

  const copyDetails = (): void => {
    const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      setCopied("refused");
      return;
    }
    const text = [
      ...facts.map(([label, value]) => `${label}: ${value}`),
      ...(medium.widget === null ? [] : [medium.widget.scriptTag]),
    ].join("\n");
    clipboard.writeText(text).then(
      () => {
        setCopied("copied");
      },
      () => {
        setCopied("refused");
      },
    );
  };

  return (
    <>
      <div className="mg-stats">
        <div className="mg-stat">
          <span className="mg-stat-value">{grouped(medium.conversationLast30Day)}</span>
          <span className="mg-stat-label">{portalText("operations", "conversations_last_30_days_4dc65e16")}</span>
        </div>
        <div className="mg-stat">
          <span className="mg-stat-value">{talkTimeText(medium.billedSecondLast30Day)}</span>
          <span className="mg-stat-label">{portalText("operations", "minutes_billed_last_30_days_b27b2e8a")}</span>
        </div>
        <div className="mg-stat">
          <span className="mg-stat-value">{grouped(medium.liveConversationCount)}</span>
          <span className="mg-stat-label">{portalText("operations", "happening_right_now_5f56731a")}</span>
        </div>
      </div>

      <div className="mg-cards">
        <div className="mg-card">
          <p className="mg-card-title mg-card-title-spaced">{portalText("operations", "connection_6512ee15")}</p>
          <dl className="mg-facts">
            {facts.map(([label, value]) => (
              <FactRow key={`${label}:${value}`} label={label}>
                {value}
              </FactRow>
            ))}
          </dl>
          {medium.medium === "voice" ? (
            <p className="mg-card-note">
              {portalText("operations", "the_trunk_the_sip_address_and_the_rotating_password_live_o_79b21e32")}{" "}
              <Link to="/numbers">{portalText("operations", "numbers_sip_dad16cb4")}</Link>{portalText("operations", "so_there_is_one_copy_of_them_calling_from_a_browser_is_not_3619bf6d")}</p>
          ) : null}
          {medium.widget === null ? null : (
            <>
              <p className="mg-card-note">
                {portalText("operations", "one_script_tag_before_the_closing_body_tag_on_any_page_whe_0ddc163a")}</p>
              <code className="mg-snippet">{medium.widget.scriptTag}</code>
            </>
          )}
          <div className="mg-card-actions">
            <Button variant="secondary" onClick={copyDetails}>
              {copied === "copied" ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_details_e42bc527")}
            </Button>
            {medium.medium === "voice" ? (
              <Link to="/numbers" className="btn btn-ghost mg-flush">
                {portalText("operations", "rotate_credentials_a99c9967")}</Link>
            ) : null}
            {copied === "refused" ? (
              <span className="mg-card-note">{portalText("operations", "the_browser_did_not_allow_copying_f4f87df9")}</span>
            ) : null}
          </div>
        </div>

        <div className="mg-card">
          <p className="mg-card-title">{portalText("operations", "behaviour_171ca038")}</p>
          <div className="mg-behaviour-field">
            <Field label={portalText("operations", "agent_that_answers_737d3bb5")}>
              {(control) => (
                <Select {...control} value={first?.voiceAgentId ?? ""} disabled>
                  {medium.binding.map((binding) => (
                    <option key={binding.channelBindingId} value={binding.voiceAgentId}>
                      {binding.voiceAgentName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="mg-behaviour">
            {BEHAVIOUR.map((one) =>
              one.code === "record" && medium.medium === "voice" && first !== undefined ? (
                <RecordingSetting key={one.code} voiceAgentId={first.voiceAgentId} />
              ) : (
                <span className="mg-setting" key={one.code}>
                  <span className="mg-setting-body">
                    <span className="mg-setting-name">
                      {one.name} <span className="mg-soon">{portalText("operations", "soon_3f934e4f")}</span>
                    </span>
                    <span className="mg-setting-dek">{one.dek}</span>
                  </span>
                  <SwitchControl
                    label={portalText("operations", "value_not_available_yet_ad66fc53", { v0: String(one.name) })}
                    labelVisible={false}
                    checked={false}
                    disabled
                    onChange={() => undefined}
                  />
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      <div className="mg-history">
        <span className="mg-history-text">
          {medium.medium === "voice"
            ? portalText("operations", "every_conversation_on_this_channel_is_listed_with_its_tran_c3694b68")
            : portalText("operations", "every_conversation_on_this_channel_is_listed_with_its_tran_76d460cf")}
        </span>
        {first === undefined ? null : (
          <Link
            to="/agents/$voiceAgentId"
            params={{ voiceAgentId: first.voiceAgentId }}
            search={{ tab: "history", range: "all", outcome: "all", q: "", conversation: "" }}
            className="btn btn-secondary"
          >
            {portalText("operations", "open_call_history_9a081204")}</Link>
        )}
      </div>
    </>
  );
}

/** A `<dt>`/`<dd>` pair, so the definition list stays flat. */
function FactRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <>
      <dt className="mg-term">{label}</dt>
      <dd className="mg-def">{children}</dd>
    </>
  );
}

function ComingSoonState({ medium }: { readonly medium: MessagingMediumDetailView }): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="c-grid c-grid-two">
      <Card>
        <CardTitle>{portalText("operations", "not_open_yet_6b3e8d60")}</CardTitle>
        {/* AMENDED 2026-10-15 (design audit): one paragraph, the design's words (`chan.soon`). */}
        <p className="c-empty-dek">
          {medium.waitlistReason ??
            portalText("operations", "value_needs_approval_from_the_provider_before_we_can_switc_409f1650", { v0: String(medium.name) })}{" "}
          {portalText("operations", "we_are_working_through_it_now_and_will_write_to_you_the_da_30d0a24b")}
        </p>
      </Card>
      <div className="c-panel c-panel-accent">
        <p className="c-empty-title">{portalText("operations", "nothing_to_prepare_c0c4f727")}</p>
        <p className="c-empty-dek">
          {portalText("operations", "when_it_opens_your_agent_answers_on_it_straight_away_using_11fef5de")}</p>
        <p className="c-kicker">{portalText("operations", "what_it_will_take_c4e9b48e")}</p>
        <ol className="mg-steps">
          {medium.step.map((step, index) => (
            <li className="mg-step" key={step.title}>
              <span className="mg-step-ordinal">{String(index + 1)}</span>
              <span>
                <span className="mg-step-title">{step.title}</span>
                <span className="mg-step-dek">{step.dek}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function NotConnectedState({ medium }: { readonly medium: MessagingMediumDetailView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const first = medium.answerableVoiceAgent[0];
  const [voiceAgentId, setVoiceAgentId] = useState(first?.voiceAgentId ?? "");

  const connect = useMutation({
    mutationFn: () => gateway.connectMessagingMedium(medium.medium, voiceAgentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.messagingMedium });
    },
  });

  const chosen = voiceAgentId === "" ? (first?.voiceAgentId ?? "") : voiceAgentId;

  return (
    <div className="c-grid c-grid-two">
      <Card>
        <CardTitle>
          {portalText("operations", "connect_in_e740eb74")}{String(medium.step.length)}{" "}
          {medium.step.length === 1 ? portalText("operations", "step_bd370d1b") : portalText("operations", "steps_6578912e")}
        </CardTitle>
        <ol className="mg-steps">
          {medium.step.map((step, index) => (
            <li className="mg-step" key={step.title}>
              <span className="mg-step-ordinal">{String(index + 1)}</span>
              <span>
                <span className="mg-step-title">{step.title}</span>
                <span className="mg-step-dek">{step.dek}</span>
              </span>
            </li>
          ))}
        </ol>
      </Card>

      <div className="mg-panel-stack">
        <Card>
          <CardTitle>{portalText("operations", "details_dc3decbb")}</CardTitle>
          {medium.medium === "voice" ? (
            <p className="c-empty-dek">
              {portalText("operations", "voice_is_connected_on_e58272b8")}<Link to="/numbers">{portalText("operations", "numbers_sip_dad16cb4")}</Link>{portalText("operations", "where_the_number_the_trunk_and_the_rotating_sip_password_l_602d4ed6")}</p>
          ) : (
            <>
              {medium.field.map((field) => (
                <Field key={field.code} label={field.label}>
                  {(control) => <Input {...control} placeholder={field.placeholder} />}
                </Field>
              ))}
              {medium.answerableVoiceAgent.length === 0 ? (
                <p className="c-empty-dek">
                  {portalText("operations", "no_agent_is_live_in_this_workspace_yet_so_there_is_nothing_2d0da829")}</p>
              ) : (
                <Field label={portalText("operations", "agent_that_answers_737d3bb5")}>
                  {(control) => (
                    <Select
                      {...control}
                      value={chosen}
                      onChange={(event) => {
                        setVoiceAgentId(event.target.value);
                      }}
                    >
                      {medium.answerableVoiceAgent.map((agent) => (
                        <option key={agent.voiceAgentId} value={agent.voiceAgentId}>
                          {agent.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}
              <Button
                variant="primary"
                block
                disabled={chosen === "" || connect.isPending}
                onClick={() => {
                  connect.mutate();
                }}
              >
                {portalText("operations", "connect_6d2b01e4")}{medium.name}
              </Button>
              {connect.isError ? (
                <p className="c-note c-note-critical" role="alert">
                  {portalText("operations", "that_could_not_be_connected_nothing_has_been_sent_and_noth_60d0c730")}</p>
              ) : null}
              <p className="c-empty-dek">
                {portalText("operations", "nothing_is_sent_until_you_connect_and_you_can_disconnect_a_687702c6")}</p>
            </>
          )}
        </Card>

        <div className="c-panel c-panel-accent">
          <p className="c-empty-title">{portalText("operations", "same_agent_no_extra_work_c50c55e3")}</p>
          <p className="c-empty-dek">
            {portalText("operations", "whatever_you_connect_answers_from_the_documents_you_alread_20f8ec27")}</p>
        </div>
      </div>
    </div>
  );
}
